import { useEffect, useState } from 'react';
import { Loader2, X, MapPin } from 'lucide-react';
import { airportApi } from '@/services/api';

interface AirportEditDialogProps {
  code: string | null;
  onClose: () => void;
  onSaved: () => void;
}

interface FormState {
  lat: string;
  lng: string;
  name: string;
  city: string;
  country: string;
}

const EMPTY: FormState = { lat: '', lng: '', name: '', city: '', country: '' };

export function AirportEditDialog({ code, onClose, onSaved }: AirportEditDialogProps) {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) return;
    setForm(EMPTY);
    setError(null);
    let cancelled = false;
    airportApi
      .get(code)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data ?? res.data;
        if (!data) return;
        setForm({
          lat: data.lat != null ? String(data.lat) : '',
          lng: data.lng != null ? String(data.lng) : '',
          name: data.name ?? '',
          city: data.city ?? '',
          country: data.country ?? '',
        });
      })
      .catch(() => {
        // 不存在记录是预期情况,沿用空表单
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (!code) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const lat = Number(form.lat);
    const lng = Number(form.lng);
    if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
      setError('纬度必须在 -90 ~ 90 之间');
      return;
    }
    if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
      setError('经度必须在 -180 ~ 180 之间');
      return;
    }
    setSubmitting(true);
    try {
      await airportApi.upsertManual(code, {
        lat,
        lng,
        name: form.name.trim() || undefined,
        city: form.city.trim() || undefined,
        country: form.country.trim() || undefined,
      });
      onSaved();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || '保存失败';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-slate-950 ring-1 ring-white/10 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-violet-600 flex items-center justify-center">
              <MapPin className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-100">补录机场坐标</h3>
              <p className="text-xs text-slate-500 font-mono">{code}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-200 hover:bg-white/5"
            aria-label="关闭"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="纬度 *" hint="-90 ~ 90">
              <input
                type="number"
                step="any"
                required
                value={form.lat}
                onChange={(e) => setForm({ ...form, lat: e.target.value })}
                className="w-full bg-white/5 ring-1 ring-white/10 rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-cyan-400/40"
                placeholder="40.0801"
              />
            </Field>
            <Field label="经度 *" hint="-180 ~ 180">
              <input
                type="number"
                step="any"
                required
                value={form.lng}
                onChange={(e) => setForm({ ...form, lng: e.target.value })}
                className="w-full bg-white/5 ring-1 ring-white/10 rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-cyan-400/40"
                placeholder="116.5846"
              />
            </Field>
          </div>
          <Field label="机场名称">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-white/5 ring-1 ring-white/10 rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-cyan-400/40"
              placeholder="Beijing Capital International"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="城市">
              <input
                type="text"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="w-full bg-white/5 ring-1 ring-white/10 rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-cyan-400/40"
                placeholder="Beijing"
              />
            </Field>
            <Field label="国家">
              <input
                type="text"
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="w-full bg-white/5 ring-1 ring-white/10 rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-cyan-400/40"
                placeholder="China"
              />
            </Field>
          </div>

          {error && (
            <div className="text-xs text-rose-300 bg-rose-500/10 ring-1 ring-rose-500/20 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs text-slate-300 hover:text-slate-100 hover:bg-white/5 rounded-md"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white rounded-md bg-gradient-to-r from-cyan-500 to-violet-600 hover:from-cyan-400 hover:to-violet-500 disabled:opacity-50"
            >
              {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
              保存
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  hint?: string;
  children: React.ReactNode;
}

function Field({ label, hint, children }: FieldProps) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs font-medium text-slate-300">{label}</span>
        {hint && <span className="text-[10px] text-slate-600">{hint}</span>}
      </div>
      {children}
    </label>
  );
}
